// src/utils/playerPicksUtils.ts
import { WinnerWithMetadata, WinnerSource } from '../types/types';
import { validateAndCleanKnownWinners } from './bracketValidator';
import { normalizeTeamNameForDisplay } from './teamUtils'; // Added import for display normalization

/**
 * Parses player picks text and extracts picks for a specific player
 * Supports both quoted player names ("Player Name") and traditional format (PlayerName)
 */
export const extractPlayerPicks = (playerPicksText: string, playerName: string): string[] => {
  if (!playerPicksText || !playerName) {
    return [];
  }

  const lines = playerPicksText.trim().split('\n');
  
  for (const line of lines) {
    if (!line.trim()) continue;
    
    // First try to match quoted player names (new format)
    let match = line.match(/^"([^"]+)"\s*=\s*(.+)$/);
    
    // If no match, try traditional format (backward compatibility)
    if (!match) {
      match = line.match(/^(\w+)\s*=\s*(.+)$/);
    }
    
    if (match && match[1] === playerName) {
      // Found the player, parse their picks
      const picksString = match[2];
      return picksString.split(',').map(pick => pick.trim()).filter(pick => pick.length > 0);
    }
  }
  
  return [];
};

/**
 * Converts a player's picks array into a WinnerWithMetadata array
 * Merges with official winners, giving official winners precedence
 * Now normalizes team names for consistent display
 */
export const convertPlayerPicksToScenario = (
  playerName: string,
  playerPicksText: string,
  officialWinners: WinnerWithMetadata[]
): { 
  winners: WinnerWithMetadata[], 
  validationError?: string,
  playerName: string 
} => {
  // Extract the specific player's picks
  const playerPicks = extractPlayerPicks(playerPicksText, playerName);
  
  if (playerPicks.length === 0) {
    return {
      winners: officialWinners,
      validationError: `No picks found for player "${playerName}"`,
      playerName
    };
  }
  
  // Create the merged winners array - start with a copy of official winners structure
  const mergedWinners: WinnerWithMetadata[] = [];
  
  // Initialize array to proper length
  for (let i = 0; i < 63; i++) {
    mergedWinners[i] = undefined as any;
  }
  
  // First pass: Add all official winners at their exact positions
  for (let i = 0; i < officialWinners.length; i++) {
    const officialWinner = officialWinners[i];
    if (officialWinner && officialWinner.source === WinnerSource.OFFICIAL && officialWinner.team) {
      mergedWinners[i] = officialWinner;
    }
  }
  
  // Second pass: Add player picks where no official winner exists
  for (let i = 0; i < Math.min(playerPicks.length, 63); i++) {
    // Skip if we already have an official winner at this position
    if (mergedWinners[i] && mergedWinners[i].source === WinnerSource.OFFICIAL) {
      continue;
    }
    
    // Add player's pick if it exists and is not empty
    if (playerPicks[i] && playerPicks[i].trim() !== '') {
      // FIXED: Normalize team name for consistent display
      const normalizedTeamName = normalizeTeamNameForDisplay(playerPicks[i].trim());
      
      mergedWinners[i] = {
        team: normalizedTeamName, // Now using normalized name
        source: WinnerSource.USER
      };
    }
  }
  
  // Convert to string array for validation
  const winnersForValidation = mergedWinners.map(winner => winner?.team || '');
  
  // Validate and clean the winners array
  const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(winnersForValidation);
  
  // Create final validated winners array
  const validatedWinners: WinnerWithMetadata[] = [];
  
  for (let i = 0; i < Math.max(cleanedWinners.length, 63); i++) {
    if (i < cleanedWinners.length && cleanedWinners[i] && cleanedWinners[i].trim() !== '') {
      // Find the original source
      const originalSource = mergedWinners[i]?.source || WinnerSource.USER;
      
      validatedWinners[i] = {
        team: cleanedWinners[i], // This is already normalized from above
        source: originalSource
      };
    }
  }
  
  // Set validation error if bracket had issues
  let validationError: string | undefined;
  if (!isValid && errorMessage) {
    validationError = `Issues found in ${playerName}'s picks: ${errorMessage}`;
  }
  
  return {
    winners: validatedWinners,
    validationError,
    playerName
  };
};

/**
 * Utility function to get all player names from player picks text
 */
export const getAllPlayerNames = (playerPicksText: string): string[] => {
  if (!playerPicksText) {
    return [];
  }

  const lines = playerPicksText.trim().split('\n');
  const playerNames: string[] = [];
  
  for (const line of lines) {
    if (!line.trim()) continue;
    
    // First try to match quoted player names (new format)
    let match = line.match(/^"([^"]+)"\s*=\s*(.+)$/);
    
    // If no match, try traditional format (backward compatibility)
    if (!match) {
      match = line.match(/^(\w+)\s*=\s*(.+)$/);
    }
    
    if (match && match[1]) {
      playerNames.push(match[1]);
    }
  }
  
  return playerNames;
};